import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Tab } from 'react-bootstrap';
import { capitalize } from 'lodash';

import styles from './styles.css';
import Shimmer from '../../../../components/shimmer';
import DateTimeDisplay from '../../../../components/date-time-display';

import {
  fetchWinLossInternalEmailStatus,
} from '../../../Dashboard/DashboardActions';

import {
  getWinLossOpportunityInternalEmails,
} from '../../../Dashboard/DashboardReducer';

const statusLabelTypes = {
  Processed: 'label-warning',
  Queued: 'label-warning',
  SubscriptionChanged: 'label-warning',
  Sent: 'label-success',
  Delivered: 'label-success',
  Opened: 'label-success',
  LinkClicked: 'label-success',
  Bounced: 'label-danger',
  Transient: 'label-danger',
  default: 'label-default'
};

/** Tab to display opportunity internal email history for checking sending and postmark status */
class InternalEmailTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      loaded: false,
    };
  }

  componentDidMount() {
    this.mounted = true;
    // this.checkProps(this.props);
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  componentDidUpdate(prevProps, _prevState) {
    // Check props if component is activating
    if (this.props.isActive && !prevProps.isActive) {
      this.checkProps(this.props);
    }
  }

  /** Load internal emails and refresh if mounted and internal emails exist and haven't been loaded */
  checkProps(props) {
    const { totalInternalEmails, object, isAccount } = props;
    const id = isAccount ? object.account_id : object.opportunity_id;
    if (this.mounted && totalInternalEmails > 0) {
      // const alreadyLoaded = this.state.loaded;
      const alreadyLoaded = false; // because when switch tabs, and make requests, there may be sumarry emails going out

      if (alreadyLoaded) {
        this.refreshTable();
      } else {
        this.setState({ loading: true }, () => {
          this.props.dispatch(fetchWinLossInternalEmailStatus(id, isAccount))
            .then(() => {
              this.setState({ loading: false, loaded: true }, () => {
                this.refreshTable();
              });
            });
        });
      }
    } else {
      this.setState({ loading: false }, () => {
        this.refreshTable();
      });
    }
  }

  /** Refresh table if measure function is passed in */
  refreshTable() {
    if (this.props.measure) {
      this.props.measure();
    }
  }

  /** Render an internal email receiver with row title and email status */
  renderEmailRecipient(email, status, title) {
    const labelType = statusLabelTypes[status] || statusLabelTypes.default;
    return (
      <tr key={email}>
        <th>{title}:</th>
        <td>
          {email} <span className={`label ${labelType}`}>{status}</span>
        </td>
      </tr>
    );
  }

  renderEmailRecipientTable(internalEmail) {
    const defaultStatus = internalEmail.postmark_message_status_default || 'Unknown';
    const emailRenders = [];

    if (internalEmail.send_to) {
      const emailAddress = internalEmail.send_to;
      const status = internalEmail.postmark_message_statuses?.[emailAddress]?.Status || defaultStatus;
      emailRenders.push(this.renderEmailRecipient(emailAddress, status, 'To'));
    }

    if (internalEmail.send_to_cc) {
      const ccRecipients = internalEmail.send_to_cc.split(',');
      ccRecipients.forEach(recipient => {
        const emailAddress = recipient;
        const status = internalEmail.postmark_message_statuses?.[emailAddress]?.Status || defaultStatus;
        emailRenders.push(this.renderEmailRecipient(emailAddress, status, 'Cc'));
      });
    }

    return (
      <table className="table table-condensed">
        <tbody>
          {emailRenders}
        </tbody>
      </table>
    );
  }

  /** Render an internal email with all recipients and time sent */
  renderInternalEmail(internalEmail) {
    if (!internalEmail) {
      return null;
    }

    const dateFormatOptions = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true,
    };
    const emailStatus = internalEmail.postmark_message_status_default || 'Unknown';
    const emailStatusLabelType = statusLabelTypes[emailStatus] || statusLabelTypes.default;
    const automationType = capitalize(internalEmail.automation_type) || 'Unknown';
    let source = null;
    if (internalEmail.source === 'sendgrid') {
      source = (<span>&nbsp;<em>Sent with</em> <strong>SendGrid</strong> <em>on</em></span>);
    } else if (internalEmail.source === 'postmark') {
      source = (<span>&nbsp;<em>Sent with</em> <strong>Postmark</strong> <em>on</em></span>);
    }
    return (
      <div key={internalEmail._id} className={`well well-sm clearfix ${styles.surveyItem}`}>
        <div className="pull-left">
          <p>
            <strong>{automationType} Email</strong> <span className={`label ${emailStatusLabelType}`}>{emailStatus}</span>
          </p>
        </div>
        <div className="pull-right clearfix">
          <p>{source} <DateTimeDisplay format="shortWithTime" value={internalEmail.postmark_submitted_at} /></p>
        </div>
        {this.renderEmailRecipientTable(internalEmail)}
      </div>
    );
  }

  /** Render internal email list */
  renderInternalEmails(internalEmails) {
    const internalEmailRenders = (internalEmails?.length !== null) && (internalEmails !== undefined) && (internalEmails.length > 0)
      ? internalEmails.map(internalEmail => this.renderInternalEmail(internalEmail))
      : null;
    return (<div>{internalEmailRenders}</div>);
  }

  /** Render loading rows with Shimmer */
  renderLoading() {
    const { totalInternalEmails } = this.props;
    return (
      <div>
        {Array.from({ length: totalInternalEmails }, (_value, key) => (
          <div key={key} className={`well well-sm clearfix ${styles.surveyItem}`}>
            <div className="pull-left">
              <p><strong>Internal Email</strong></p>
            </div>
            <div className="pull-right clearfix">
              <p><Shimmer value='XXXXXXXXXXXXXXXX' /></p>
            </div>
            <table className="table table-condensed">
              <tbody>
                <tr>
                  <th>To:</th>
                  <td><Shimmer value='XXXXXXXXXXXXXXXXXXXXXXXXXX' /></td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  }

  /** Render loading or internal emails if they have been loaded */
  renderResults() {
    const { internalEmails } = this.props;
    const { loading } = this.state;
    let results = null;
    if (loading) {
      results = this.renderLoading();
    } else if (internalEmails) {
      results = this.renderInternalEmails(internalEmails);
    }
    return results;
  }

  /** Display default message to be used when receiving no data */
  renderEmpty() {
    const objectName = this.props.isAccount ? 'account' : 'opportunity';
    return (
      <div className='text-left'>
        <div className='well well-sm'>
          No internal emails have been sent for this {objectName}.
        </div>
      </div>
    );
  }

  /** Render tab with results, loading Shimmers, or empty message */
  render() {
    const { eventKey, title, totalInternalEmails, className } = this.props;
    return (
      <Tab eventKey={eventKey} title={title}>
        <div className={`${styles.section} ${className}`}>
          {totalInternalEmails === 0 && this.renderEmpty()}
          {totalInternalEmails > 0 && this.renderResults()}
        </div>
      </Tab>
    );
  }
}

function mapStateToProps(state, ownProps) {
  const isAccount = ownProps.isAccount;
  const id = isAccount ? ownProps.object.account_id : ownProps.object.opportunity_id;
  const internalEmails = getWinLossOpportunityInternalEmails(state, id) || null;
  return {
    internalEmails,
  };
}

InternalEmailTab.propTypes = {
  dispatch: PropTypes.func.isRequired,
  eventKey: PropTypes.string.isRequired,
  title: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
  ]).isRequired,
  object: PropTypes.object.isRequired,
  totalInternalEmails: PropTypes.number,
  internalEmails: PropTypes.array,
  className: PropTypes.string,
  measure: PropTypes.func,
  isActive: PropTypes.bool,
  isAccount: PropTypes.bool,
};

InternalEmailTab.defaultProps = {
  totalInternalEmails: 0,
  className: null,
  measure: () => {},
  isActive: false,
  internalEmails: null,
  isAccount: false,
};

export default connect(mapStateToProps)(InternalEmailTab);
